import {
  certifyCallsLiveDeviceEvidence,
  type CallsLiveDeviceCertificationResult,
} from './CallsLiveDeviceCertificationEngine';

export type CallsProtectedRuntimeOperation =
  | 'start-outgoing-call'
  | 'deliver-incoming-call'
  | 'answer-incoming-call'
  | 'connect-call-media'
  | 'resume-call'
  | 'release-call';

export type CallsRuntimeEnforcementDecision =
  | 'allow'
  | 'block';

export type CallsRuntimeEnforcementReasonCode =
  | 'CALLS_RUNTIME_CERTIFIED'
  | 'CALLS_RUNTIME_NOT_CERTIFIED'
  | 'CALLS_RUNTIME_CERTIFICATION_STALE'
  | 'CALLS_RUNTIME_CERTIFICATION_UNAVAILABLE'
  | 'CALLS_RUNTIME_CERTIFICATION_INVALID'
  | 'CALLS_RUNTIME_ENFORCEMENT_ERROR';

export interface CallsRuntimeCertificationEnforcementResult {
  readonly gate: 'CallsRuntimeCertificationEnforcementGate';
  readonly governor: 'CallsGovernor';
  readonly domain: 'calls';
  readonly operation: CallsProtectedRuntimeOperation;
  readonly decision: CallsRuntimeEnforcementDecision;
  readonly allowed: boolean;
  readonly reasonCode: CallsRuntimeEnforcementReasonCode;
  readonly reason: string;
  readonly certificationStatus: string;
  readonly certificationReasonCode: string;
  readonly certificationIssuedAt: string | null;
  readonly evaluatedAt: string;
}

const MAX_CERTIFICATION_AGE_MS = 15 * 60 * 1000;

function isValidTimestamp(value: string | null): boolean {
  return value !== null && Number.isFinite(Date.parse(value));
}

function isCertificationCurrent(
  certificationIssuedAt: string | null,
  evaluatedAt: string,
): boolean {
  if (!isValidTimestamp(certificationIssuedAt)) {
    return false;
  }

  const issuedAtMs = Date.parse(certificationIssuedAt as string);
  const evaluatedAtMs = Date.parse(evaluatedAt);

  if (!Number.isFinite(evaluatedAtMs)) {
    return false;
  }

  const ageMs = evaluatedAtMs - issuedAtMs;

  return ageMs >= 0 && ageMs <= MAX_CERTIFICATION_AGE_MS;
}

function mapCertificationReason(
  certification: CallsLiveDeviceCertificationResult,
): CallsRuntimeEnforcementReasonCode {
  switch (certification.reasonCode) {
    case 'CALLS_LIVE_DEVICE_EVIDENCE_STALE':
      return 'CALLS_RUNTIME_CERTIFICATION_STALE';

    case 'CALLS_LIVE_DEVICE_VERIFICATION_UNAVAILABLE':
      return 'CALLS_RUNTIME_CERTIFICATION_UNAVAILABLE';

    case 'CALLS_LIVE_DEVICE_EVIDENCE_INCOMPLETE':
    case 'CALLS_LIVE_DEVICE_VERIFICATION_FAILED':
      return 'CALLS_RUNTIME_NOT_CERTIFIED';

    case 'CALLS_LIVE_DEVICE_EVIDENCE_VERIFIED':
      return certification.certified
        ? 'CALLS_RUNTIME_CERTIFIED'
        : 'CALLS_RUNTIME_CERTIFICATION_INVALID';
  }
}

function createResult(
  operation: CallsProtectedRuntimeOperation,
  decision: CallsRuntimeEnforcementDecision,
  reasonCode: CallsRuntimeEnforcementReasonCode,
  reason: string,
  certification: CallsLiveDeviceCertificationResult | null,
  evaluatedAt: string,
): CallsRuntimeCertificationEnforcementResult {
  return Object.freeze({
    gate: 'CallsRuntimeCertificationEnforcementGate',
    governor: 'CallsGovernor',
    domain: 'calls',
    operation,
    decision,
    allowed: decision === 'allow',
    reasonCode,
    reason,
    certificationStatus: certification?.status ?? 'unavailable',
    certificationReasonCode:
      certification?.reasonCode ?? 'unavailable',
    certificationIssuedAt:
      certification?.certificationIssuedAt ?? null,
    evaluatedAt,
  });
}

/**
 * Evaluates a protected Calls operation against an explicit CG-023
 * certification result.
 *
 * The gate fails closed. Missing, invalid, stale, or unsuccessful
 * certification always blocks the protected operation.
 */
export function evaluateCallsRuntimeCertification(
  operation: CallsProtectedRuntimeOperation,
  certification: CallsLiveDeviceCertificationResult,
  evaluatedAt: string = new Date().toISOString(),
): CallsRuntimeCertificationEnforcementResult {
  if (
    certification.status !== 'certified' ||
    certification.certified !== true ||
    certification.reasonCode !==
      'CALLS_LIVE_DEVICE_EVIDENCE_VERIFIED'
  ) {
    const reasonCode = mapCertificationReason(certification);

    return createResult(
      operation,
      'block',
      reasonCode,
      `Calls runtime operation "${operation}" was blocked because live-device certification is not valid: ${certification.reason}`,
      certification,
      evaluatedAt,
    );
  }

  if (
    !isCertificationCurrent(
      certification.certificationIssuedAt,
      evaluatedAt,
    )
  ) {
    return createResult(
      operation,
      'block',
      'CALLS_RUNTIME_CERTIFICATION_STALE',
      `Calls runtime operation "${operation}" was blocked because the certification decision is stale or has an invalid timestamp.`,
      certification,
      evaluatedAt,
    );
  }

  return createResult(
    operation,
    'allow',
    'CALLS_RUNTIME_CERTIFIED',
    `Calls runtime operation "${operation}" is allowed by current CG-023 live-device certification.`,
    certification,
    evaluatedAt,
  );
}

/**
 * Runs CG-023 and immediately evaluates the requested protected operation.
 *
 * This function produces the enforcement decision only. CG-024 does not
 * start, answer, connect, end, or otherwise mutate a call session.
 */
export function enforceCallsRuntimeCertification(
  operation: CallsProtectedRuntimeOperation,
): CallsRuntimeCertificationEnforcementResult {
  const evaluatedAt = new Date().toISOString();

  try {
    const certification = certifyCallsLiveDeviceEvidence();

    return evaluateCallsRuntimeCertification(
      operation,
      certification,
      evaluatedAt,
    );
  } catch {
    return createResult(
      operation,
      'block',
      'CALLS_RUNTIME_ENFORCEMENT_ERROR',
      `Calls runtime operation "${operation}" was blocked because certification enforcement threw an exception.`,
      null,
      evaluatedAt,
    );
  }
}
